'use client';

import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { FormItem, PasswordInput, SessionInput } from '@flipster/ui';

import { ContinueWithApple } from '@web/app/[locale]/(web)/_auth/continue-with-apple';
import { ContinueWithGoogle } from '@web/app/[locale]/(web)/_auth/continue-with-google';
import { ContinueWithPasskey } from '@web/app/[locale]/(web)/_auth/continue-with-passkey';
import { useBlockedRegion } from '@web/hooks/useBlockedRegion';
import { LoginSchema } from '@web/utils/session-validator';

import { restrictedMessage } from '../_constants';
import { divider, formItem, h1, header, socialButtonGroup, wrapper } from '../_style';

interface Props {
  loading: boolean;
  disabled: boolean;
  onPasskeyButtonClick?: () => void;
}

const LoginForm = ({ disabled, loading, onPasskeyButtonClick }: Props) => {
  const t = useTranslations();
  const { isRestrictedRegion } = useBlockedRegion();
  const {
    register,
    clearErrors,
    formState: { errors },
  } = useFormContext<LoginSchema>();

  const clearServerErrors = () => {
    if (errors.email?.type === 'server') clearErrors('email');
    if (errors.password?.type === 'server') clearErrors('password');
  };

  return (
    <div className={wrapper}>
      <header className={header}>
        <h1 className={h1}>
          <FormattedMessage id='Autogen_common.r251126.s_Log_In' />
        </h1>
      </header>
      <FormItem
        label={t('Autogen_common.r251126.s_E_mail')}
        className={formItem}
        error={errors.email?.type === 'server' ? errors.email?.message : undefined}
      >
        <SessionInput placeholder='name@flipster.io' {...register('email', { onChange: clearServerErrors })} />
      </FormItem>
      <FormItem
        label={t('Autogen_common.r251126.s_Password')}
        className={formItem}
        error={errors.password?.type === 'server' ? errors.password?.message : undefined}
      >
        <PasswordInput
          placeholder={t('Autogen_common.r251126.s_Password')}
          {...register('password', { onChange: clearServerErrors })}
        />
      </FormItem>
      {isRestrictedRegion && (
        <p className={css({ textStyle: 'Text/S/Regular', pb: 6, color: 'content.default.level-5' })}>
          {restrictedMessage}
        </p>
      )}
      <div className={flex({ mt: 24, justify: 'center' })}>
        <TextButton
          color='accent'
          size='large'
          as='a'
          href='/forgotPassword'
          label={t('Autogen_common.r251126.s_Forgot_your_password')}
        />
      </div>
      <Button
        htmlType='submit'
        isLoading={loading}
        disabled={disabled}
        label={t('Autogen_common.r251126.s_Log_In')}
        size='large'
        css={{ w: '100%', mt: 32 }}
      />
      <div className={divider}>OR</div>
      <div className={socialButtonGroup}>
        {onPasskeyButtonClick && (
          <ContinueWithPasskey
            label={t('Autogen_common.r251126.s_Log_in_with_Passkey')}
            onClick={onPasskeyButtonClick}
            disabled={loading}
          />
        )}
        <ContinueWithGoogle label={t('Autogen_common.r251126.s_Log_in_with_Google')} disabled={loading} />
        <ContinueWithApple label={t('Autogen_common.r251126.s_Log_in_with_Apple')} disabled={loading} />
      </div>
    </div>
  );
};

export default LoginForm;
